import { useParams, Link } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { externalSupabase } from "@/lib/externalSupabase";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
  Loader2, ArrowLeft, MapPin, BedDouble, Home, BadgeCheck,
  ExternalLink, TrendingDown, Shield, Calendar
} from "lucide-react";
import { Button } from "@/components/ui/button";
import PropertyDetailMap from "@/components/PropertyDetailMap";
import PropertyGallery from "@/components/PropertyGallery";
import { useToast } from "@/hooks/use-toast";

interface PropertyDetail {
  id: string;
  masked_title: string | null;
  original_name: string | null;
  image_url: string | null;
  city: string | null;
  country: string | null;
  bedrooms: number | null;
  property_type: string | null;
  description: string | null;
  owner_website: string | null;
  is_verified: boolean | null;
  airbnb_url: string;
  finding_method: string | null;
  created_at: string | null;
  latitude: number | null;
  longitude: number | null;
  street_address: string | null;
  agoda_url: string | null;
  gallery_urls: string[] | null;
}

const PropertyDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const [property, setProperty] = useState<PropertyDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const fetchProperty = async () => {
      if (!id) { setNotFound(true); setLoading(false); return; }
      const { data, error } = await externalSupabase
        .from("properties")
        .select("*")
        .eq("id", id)
        .maybeSingle();

      if (error || !data) {
        setNotFound(true);
      } else {
        setProperty(data as PropertyDetail);
      }
      setLoading(false);
    };
    fetchProperty();
  }, [id]);

  const handleSyncGallery = useCallback(async () => {
    if (!property) return;
    setIsSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke("sync-gallery", {
        body: { airbnb_url: property.airbnb_url, property_id: property.id },
      });
      if (error) throw error;

      // If function said to skip (not enough quality images), don't overwrite
      if (data?.skipped) {
        toast({ title: "Skipped", description: "Not enough high-quality property images found. Existing photos preserved.", variant: "destructive" });
        setIsSyncing(false);
        return;
      }

      if (!data?.image_url && (!data?.gallery_urls || data.gallery_urls.length === 0)) {
        toast({ title: "No photos found", description: "The scraper couldn't find gallery images for this listing.", variant: "destructive" });
        setIsSyncing(false);
        return;
      }

      // Save original hero to localStorage before overwriting
      if (property.image_url) {
        const key = `original_hero_${property.id}`;
        if (!localStorage.getItem(key)) {
          localStorage.setItem(key, property.image_url);
        }
      }

      const updatePayload: Record<string, any> = {};
      if (data.gallery_urls?.length > 0) updatePayload.gallery_urls = data.gallery_urls;
      if (data.image_url) updatePayload.image_url = data.image_url;

      await (externalSupabase.from("properties") as any).update(updatePayload).eq("id", property.id);

      setProperty(prev => prev ? {
        ...prev,
        image_url: data.image_url || prev.image_url,
        gallery_urls: data.gallery_urls?.length > 0 ? data.gallery_urls : prev.gallery_urls,
      } : prev);
      toast({ title: "Gallery synced!", description: `${(data.gallery_urls?.length || 0) + (data.image_url ? 1 : 0)} photos loaded.` });
    } catch (err: any) {
      console.error("Sync gallery error:", err);
      toast({ title: "Sync failed", description: err.message || "Could not fetch photos.", variant: "destructive" });
    } finally {
      setIsSyncing(false);
    }
  }, [property, toast]);

  const handleResetToOriginal = useCallback(async () => {
    if (!property) return;
    const key = `original_hero_${property.id}`;
    const originalUrl = localStorage.getItem(key);
    if (!originalUrl) {
      toast({ title: "No original found", description: "No saved original hero image for this property.", variant: "destructive" });
      return;
    }
    await (externalSupabase.from("properties") as any)
      .update({ image_url: originalUrl })
      .eq("id", property.id);
    setProperty(prev => prev ? { ...prev, image_url: originalUrl } : prev);
    toast({ title: "Hero image restored", description: "Reverted to the original hero image." });
  }, [property, toast]);

  const handleWipeAndResync = useCallback(async () => {
    if (!property) return;
    setIsSyncing(true);
    try {
      // Wipe current images
      await (externalSupabase.from("properties") as any)
        .update({ image_url: null, gallery_urls: [] })
        .eq("id", property.id);
      setProperty(prev => prev ? { ...prev, image_url: null, gallery_urls: [] } : prev);

      // Re-run sync with improved scraper
      const { data, error } = await supabase.functions.invoke("sync-gallery", {
        body: { airbnb_url: property.airbnb_url, property_id: property.id },
      });
      if (error) throw error;

      if (data?.skipped || (!data?.image_url && (!data?.gallery_urls || data.gallery_urls.length === 0))) {
        toast({ title: "No quality photos found", description: "Scraper couldn't find valid property images after filtering.", variant: "destructive" });
        setIsSyncing(false);
        return;
      }

      const updatePayload: Record<string, any> = {};
      if (data.gallery_urls?.length > 0) updatePayload.gallery_urls = data.gallery_urls;
      if (data.image_url) updatePayload.image_url = data.image_url;

      await (externalSupabase.from("properties") as any).update(updatePayload).eq("id", property.id);
      setProperty(prev => prev ? {
        ...prev,
        image_url: data.image_url || null,
        gallery_urls: data.gallery_urls || [],
      } : prev);
      toast({ title: "Wiped & re-synced!", description: `${(data.gallery_urls?.length || 0) + (data.image_url ? 1 : 0)} clean photos loaded.` });
    } catch (err: any) {
      console.error("Wipe & resync error:", err);
      toast({ title: "Re-sync failed", description: err.message, variant: "destructive" });
    } finally {
      setIsSyncing(false);
    }
  }, [property, toast]);

  const handleSetAsHero = useCallback(async (imageUrl: string) => {
    if (!property) return;
    // Swap: current hero goes into gallery, clicked image becomes hero
    const currentGallery = property.gallery_urls || [];
    const newGallery = [
      ...(property.image_url ? [property.image_url] : []),
      ...currentGallery.filter(u => u !== imageUrl),
    ];

    // Update DB
    await (externalSupabase.from("properties") as any)
      .update({ image_url: imageUrl, gallery_urls: newGallery })
      .eq("id", property.id);

    // Update local state
    setProperty(prev => prev ? {
      ...prev,
      image_url: imageUrl,
      gallery_urls: newGallery,
    } : prev);
    toast({ title: "Hero image updated" });
  }, [property, toast]);

  const handleRemoveImage = useCallback(async (imageUrl: string) => {
    if (!property) return;
    const newGallery = (property.gallery_urls || []).filter(u => u !== imageUrl);
    await (externalSupabase.from("properties") as any)
      .update({ gallery_urls: newGallery })
      .eq("id", property.id);
    setProperty(prev => prev ? { ...prev, gallery_urls: newGallery } : prev);
    toast({ title: "Image removed" });
  }, [property, toast]);

  const displayTitle = property?.masked_title || property?.original_name || "Untitled Property";
  const locationText = [property?.city, property?.country].filter(Boolean).join(", ");

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <main className="flex-1 pt-24 pb-16">
        {/* Loading */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary mb-3" />
            <p className="text-sm text-muted-foreground">Loading property...</p>
          </div>
        )}

        {/* Not Found */}
        {notFound && !loading && (
          <div className="text-center py-20">
            <div className="w-16 h-16 bg-accent rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Home className="w-7 h-7 text-accent-foreground" />
            </div>
            <h1 className="font-display text-2xl font-bold mb-2">Listing Not Found</h1>
            <p className="text-sm text-muted-foreground mb-6">
              This property doesn't exist or may have been removed.
            </p>
            <Button asChild>
              <Link to="/listings">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Listings
              </Link>
            </Button>
          </div>
        )}

        {/* Property Detail */}
        {property && !loading && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            {/* Breadcrumb */}
            <div className="container mx-auto px-4 max-w-6xl mb-5">
              <Link to="/listings" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
                <ArrowLeft className="w-4 h-4" /> Back to listings
              </Link>
            </div>

            {/* Premium Gallery */}
            <PropertyGallery
              mainImage={property.image_url}
              galleryUrls={property.gallery_urls || []}
              title={displayTitle}
              isVerified={property.is_verified === true}
              onSyncGallery={handleSyncGallery}
              isSyncing={isSyncing}
              isAdmin={true}
              onSetAsHero={handleSetAsHero}
              onRemoveImage={handleRemoveImage}
            />
            {/* Admin tools */}
            <div className="container mx-auto px-4 max-w-6xl mt-3 mb-2 flex gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={handleWipeAndResync}
                disabled={isSyncing}
                className="text-xs border-destructive text-destructive hover:bg-destructive/10"
              >
                {isSyncing ? "Re-syncing…" : "Wipe & Re-sync Photos"}
              </Button>
              {localStorage.getItem(`original_hero_${property.id}`) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleResetToOriginal}
                  className="text-xs border-destructive text-destructive hover:bg-destructive/10"
                >
                  Reset Hero to Original
                </Button>
              )}
            </div>

            {/* Content + Sidebar */}
            <div className="container mx-auto px-4 max-w-6xl">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left: Info */}
                <div className="lg:col-span-2 space-y-8">
                  {/* Header Info */}
                  <div>
                    <h1 className="font-display text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-3">
                      {displayTitle}
                    </h1>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground">
                      {property.property_type && (
                        <span className="flex items-center gap-1.5">
                          <Home className="w-4 h-4 text-primary" /> {property.property_type}
                        </span>
                      )}
                      {property.bedrooms != null && (
                        <span className="flex items-center gap-1.5">
                          <BedDouble className="w-4 h-4 text-primary" /> {property.bedrooms} {property.bedrooms === 1 ? "Bedroom" : "Bedrooms"}
                        </span>
                      )}
                      {locationText && (
                        <span className="flex items-center gap-1.5">
                          <MapPin className="w-4 h-4 text-primary" /> {locationText}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="border-t border-border" />

                  {/* Description */}
                  <div>
                    <h2 className="font-display text-lg font-semibold mb-3">About this property</h2>
                    {property.description ? (
                      <p className="text-muted-foreground leading-relaxed whitespace-pre-line">{property.description}</p>
                    ) : (
                      <p className="text-muted-foreground/60 italic text-sm">No description available yet. We're working on adding more details for this listing.</p>
                    )}
                  </div>

                  {/* Location Detail */}
                  {locationText && (
                    <>
                      <div className="border-t border-border" />
                      <div>
                        <h2 className="font-display text-lg font-semibold mb-3">Location</h2>
                        <div className="flex items-start gap-3 bg-card border rounded-xl p-4 mb-4">
                          <MapPin className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                          <div>
                            <p className="font-medium text-foreground">
                              {property.street_address || locationText}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">Exact address shared after booking confirmation.</p>
                          </div>
                        </div>

                        {/* Interactive Map */}
                        {property.latitude != null && property.longitude != null && (
                          <PropertyDetailMap
                            latitude={property.latitude}
                            longitude={property.longitude}
                            title={displayTitle}
                            imageUrl={property.image_url}
                            propertyId={property.id}
                            streetAddress={property.street_address}
                            onAddressResolved={(addr) => {
                              setProperty((prev) => prev ? { ...prev, street_address: addr } : prev);
                              // Persist to DB
                              externalSupabase
                                .from("properties")
                                .update({ street_address: addr })
                                .eq("id", property.id)
                                .then(({ error }) => {
                                  if (error) console.error("Failed to save address:", error);
                                });
                            }}
                          />
                        )}
                      </div>
                    </>
                  )}

                </div>

                {/* Right: Deal Box Sidebar */}
                <div className="lg:col-span-1">
                  <div className="sticky top-28 space-y-4">
                    {/* Deal Card */}
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2, duration: 0.4 }}
                      className="bg-card border rounded-2xl p-6 shadow-lg"
                    >
                      <div className="flex items-center gap-2 mb-5">
                        <div className="w-8 h-8 rounded-full bg-savings/10 flex items-center justify-center">
                          <TrendingDown className="w-4 h-4 text-savings" />
                        </div>
                        <h3 className="font-display font-bold text-foreground">Potential Savings</h3>
                      </div>

                      {/* Savings breakdown */}
                      <div className="bg-accent/50 rounded-xl p-4 mb-5">
                        <div className="flex justify-between items-center text-sm mb-2">
                          <span className="text-muted-foreground">Airbnb price (est.)</span>
                          <span className="text-muted-foreground">—</span>
                        </div>
                        <div className="flex justify-between items-center text-sm mb-3">
                          <span className="text-muted-foreground">Direct price (est.)</span>
                          <span className="text-muted-foreground">—</span>
                        </div>
                        <div className="border-t border-border pt-3 flex justify-between items-center">
                          <span className="font-semibold text-foreground text-sm">You could save</span>
                          <span className="font-bold text-savings text-lg">10–20%</span>
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-2">
                          Typical savings when booking direct. Actual price depends on dates.
                        </p>
                      </div>

                      {/* CTA Buttons */}
                      <div className="space-y-3">
                        {property.owner_website && (
                          <Button
                            asChild
                            className="w-full bg-primary text-primary-foreground hover:bg-primary/85 active:scale-[0.98] transition-all duration-150 shadow-sm hover:shadow-md h-12 text-sm font-semibold"
                          >
                            <a href={property.owner_website} target="_blank" rel="noopener noreferrer">
                              <Shield className="w-4 h-4 mr-2" />
                              Book Direct (Verified)
                            </a>
                          </Button>
                        )}

                        {property.agoda_url && (
                          <Button
                            asChild
                            variant="outline"
                            className="w-full h-12 text-sm font-semibold hover:bg-accent active:scale-[0.98] transition-all duration-150"
                          >
                            <a href={property.agoda_url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="w-4 h-4 mr-2" />
                              View on Agoda
                            </a>
                          </Button>
                        )}

                        <Button
                          asChild
                          variant="outline"
                          className="w-full h-12 text-sm font-semibold hover:bg-accent active:scale-[0.98] transition-all duration-150"
                        >
                          <a href={property.airbnb_url} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="w-4 h-4 mr-2" />
                            View on Airbnb
                          </a>
                        </Button>
                      </div>

                      {!property.owner_website && (
                        <p className="text-xs text-muted-foreground text-center mt-4 italic">
                          Direct booking link not yet verified for this property.
                        </p>
                      )}
                    </motion.div>

                    {/* Trust badges */}
                    <div className="bg-card border rounded-2xl p-5">
                      <div className="space-y-3 text-xs text-muted-foreground">
                        <div className="flex items-center gap-2.5">
                          <BadgeCheck className="w-4 h-4 text-primary shrink-0" />
                          <span>{property.is_verified ? "Verified direct booking site" : "Verification in progress"}</span>
                        </div>
                        <div className="flex items-center gap-2.5">
                          <Calendar className="w-4 h-4 text-primary shrink-0" />
                          <span>Added {property.created_at ? new Date(property.created_at).toLocaleDateString("en-US", { month: "short", year: "numeric" }) : "recently"}</span>
                        </div>
                        <div className="flex items-center gap-2.5">
                          <TrendingDown className="w-4 h-4 text-primary shrink-0" />
                          <span>Save on platform fees by booking direct</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default PropertyDetailPage;
